/** A4 星图用独立的 2D 画布排版，导出不受屏幕尺寸、镜头和 WebGL 画质影响。 */
import {
  SPIRAL, addDays, dayAnchor, daysBetween, hash01, layoutStars, localDateOf,
  type LocalDate, type PhotoDto, type SnapshotResponse,
} from '@star-sea/shared';

export const POSTER_WIDTH = 2480;
export const POSTER_HEIGHT = 3508;
export const POSTER_DPI = 300;

export interface PosterOptions {
  /** 调用方按导出范围和可见性筛过的照片；缺省只用普通照片。 */
  photos?: readonly PhotoDto[];
  rangeLabel?: string;
  today?: LocalDate;
}

export interface PosterPoint {
  photo: PhotoDto;
  x: number;
  y: number;
}

/** 保留完整星海里同一天的添加顺序，筛选年份或主题不会把剩下的星挪到星团中心。 */
export function posterLayout(snapshot: SnapshotResponse, selected: readonly PhotoDto[] = snapshot.photos): {
  points: PosterPoint[]; extent: number; first?: LocalDate; last?: LocalDate;
} {
  const wanted = new Set(selected.map(photo => photo.id));
  const all = new Map(snapshot.photos.map(photo => [photo.id, photo]));
  for (const photo of selected) if (!all.has(photo.id)) all.set(photo.id, photo);
  const source = [...all.values()].sort((a, b) => a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const positions = layoutStars(source, snapshot.space.since);
  const points: PosterPoint[] = [];
  let extent = SPIRAL.r0, first: LocalDate | undefined, last: LocalDate | undefined;
  source.forEach((photo, index) => {
    if (!wanted.has(photo.id)) return;
    const x = positions[index * 3]!, y = positions[index * 3 + 2]!;
    if (!Number.isFinite(x) || !Number.isFinite(y)) throw new Error('照片日期跨度太大，暂时无法生成星图');
    points.push({ photo, x, y });
    extent = Math.max(extent, Math.hypot(x, y));
    if (!first || photo.date < first) first = photo.date;
    if (!last || photo.date > last) last = photo.date;
  });
  return { points, extent, first, last };
}

const FONT = '"PingFang SC", "Microsoft YaHei", sans-serif';
const INK = '#293541', MUTED = '#7a7e80', PAPER = '#f6f2e9';
const CX = POSTER_WIDTH / 2, CY = 1594, RADIUS = 918;

function text(ctx: CanvasRenderingContext2D, value: string, y: number, size: number, color = INK, maxWidth = 1980): void {
  // 用户的称呼和标题直接画字；换行压成空格，过长时缩小并省略，避免越过纸边。
  let line = value.replace(/\s+/g, ' ').trim();
  let fontSize = size;
  ctx.font = `${fontSize}px ${FONT}`;
  while (ctx.measureText(line).width > maxWidth && fontSize > size * 0.6) {
    fontSize -= 2;
    ctx.font = `${fontSize}px ${FONT}`;
  }
  if (ctx.measureText(line).width > maxWidth) {
    const letters = [...line];
    while (letters.length && ctx.measureText(`${letters.join('')}…`).width > maxWidth) letters.pop();
    line = `${letters.join('')}…`;
  }
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(line, CX, y);
}

function dot(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string): void {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}

export function renderPoster(snapshot: SnapshotResponse, options: PosterOptions = {}): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = POSTER_WIDTH;
  canvas.height = POSTER_HEIGHT;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('这台设备暂时无法生成海报，请换一个浏览器再试');
  const layout = posterLayout(snapshot, options.photos);
  const today = options.today ?? localDateOf(Date.now());
  const days = daysBetween(snapshot.space.since, today) + 1;
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  text(ctx, 'THE SKY WE MADE', 230, 29, MUTED);
  text(ctx, snapshot.space.name || '我们的星海', 366, 106);
  const names = snapshot.members.map(member => member.displayName).join('  ·  ');
  text(ctx, names || '你  ·  我', 502, 43, MUTED);

  const sky = ctx.createRadialGradient(CX, CY - 260, 0, CX, CY, RADIUS);
  sky.addColorStop(0, '#1c2c3c');
  sky.addColorStop(0.68, '#121e2b');
  sky.addColorStop(1, '#0e1822');
  dot(ctx, CX, CY, RADIUS, '#0e1822');
  ctx.fillStyle = sky;
  ctx.fill();

  const scale = (RADIUS - 114) / layout.extent;
  ctx.save();
  ctx.beginPath();
  ctx.arc(CX, CY, RADIUS - 3, 0, Math.PI * 2);
  ctx.clip();

  // 月份刻度沿着共享布局的方向：一月在上，时间顺时针走。
  for (let month = 0; month < 12; month++) {
    const angle = month / 12 * Math.PI * 2;
    const sx = Math.sin(angle), sy = -Math.cos(angle);
    ctx.beginPath();
    ctx.moveTo(CX + sx * (RADIUS - 36), CY + sy * (RADIUS - 36));
    ctx.lineTo(CX + sx * (RADIUS - 49), CY + sy * (RADIUS - 49));
    ctx.strokeStyle = '#71818d';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  if (layout.first && layout.last) {
    const span = daysBetween(layout.first, layout.last);
    const samples = Math.min(2400, Math.max(1, span));
    ctx.beginPath();
    for (let i = 0; i <= samples; i++) {
      const position = dayAnchor(addDays(layout.first, Math.round(span * i / samples)), snapshot.space.since);
      const x = CX + position.x * scale, y = CY + position.z * scale;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = '#72869955';
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }

  const tints = new Map(snapshot.members.map(member => [member.id, member.tint]));
  // 不用加法混合：密集的一天不会把整团星叠成一块白斑。
  for (const point of layout.points) {
    const x = CX + point.x * scale, y = CY + point.y * scale;
    const color = tints.get(point.photo.createdBy) === 'rose' ? '#ebbdc2' : '#eddbac';
    const radius = 2.0 + hash01(point.photo.id, 61) * 2.3;
    dot(ctx, x, y, radius * 2.6, `${color}18`);
    dot(ctx, x, y, radius, color);
  }
  // 双星核只是两个人的署名，不加入照片数量。
  ctx.strokeStyle = '#a9b7c35a';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.ellipse(CX, CY, 29, 12, -0.45, 0, Math.PI * 2);
  ctx.stroke();
  dot(ctx, CX - 22, CY + 9, 6.5, '#eddbac');
  dot(ctx, CX + 22, CY - 9, 6.5, '#ebbdc2');
  if (!layout.points.length) text(ctx, '星海，正等第一颗星', CY + 112, 31, '#acb7c3');
  ctx.restore();

  text(ctx, '01', CY - RADIUS - 32, 23, MUTED);
  text(ctx, '一年一圈，每颗星都是一张回忆', 2610, 31, MUTED);
  text(ctx, days > 0 ? `在一起的第 ${days.toLocaleString('zh-CN')} 天` : `距离我们的第一天，还有 ${1 - days} 天`, 2830, 69);
  text(ctx, `从 ${snapshot.space.since.replaceAll('-', ' . ')} 开始`, 2932, 34, MUTED);
  ctx.strokeStyle = '#cac7c0';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(CX - 165, 3043);
  ctx.lineTo(CX + 165, 3043);
  ctx.stroke();
  const range = options.rangeLabel || (layout.first && layout.last ? `${layout.first} — ${layout.last}` : '全部回忆');
  text(ctx, `${range}  ·  ${layout.points.length.toLocaleString('zh-CN')} 颗星`, 3130, 34, MUTED);
  text(ctx, '和你一起，点亮整片星海', 3322, 32);
  return canvas;
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('海报生成失败，可能是设备内存不足，请稍后重试')), type, quality);
  });
}

function concat(parts: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const output = new Uint8Array(parts.reduce((size, part) => size + part.length, 0));
  let offset = 0;
  for (const part of parts) { output.set(part, offset); offset += part.length; }
  return output;
}

const encoder = new TextEncoder();

/** PNG 的 CRC 覆盖类型和数据，长度字段不参与。 */
function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** Canvas 默认的 96 DPI 元数据不能用于印刷；替换为 PNG 的每米像素数，并保留图像数据。 */
export function pngWithDpi(png: Uint8Array, dpi = POSTER_DPI): Uint8Array<ArrayBuffer> {
  const signature = [137, 80, 78, 71, 13, 10, 26, 10];
  if (!signature.every((byte, i) => png[i] === byte)) throw new Error('海报不是有效的 PNG');
  const density = Math.round(dpi / 0.0254);
  if (!Number.isFinite(density) || density < 1 || density > 0xffffffff) throw new RangeError('DPI 超出范围');
  const chunk = new Uint8Array(21), view = new DataView(chunk.buffer);
  view.setUint32(0, 9);
  chunk.set(encoder.encode('pHYs'), 4);
  view.setUint32(8, density); view.setUint32(12, density); chunk[16] = 1;
  view.setUint32(17, crc32(chunk.subarray(4, 17)));
  const parts: Uint8Array[] = [png.subarray(0, 8)];
  const input = new DataView(png.buffer, png.byteOffset, png.byteLength);
  let offset = 8, hasHeader = false, hasImage = false, ended = false;
  while (offset + 12 <= png.length) {
    const size = input.getUint32(offset), end = offset + 12 + size;
    if (end > png.length) throw new Error('PNG 数据不完整');
    const type = String.fromCharCode(...png.subarray(offset + 4, offset + 8));
    if (!hasHeader && (type !== 'IHDR' || size !== 13)) throw new Error('PNG 缺少图像头');
    if (type !== 'pHYs') parts.push(png.subarray(offset, end));
    if (!hasHeader) { parts.push(chunk); hasHeader = true; }
    if (type === 'IDAT') hasImage = true;
    offset = end;
    if (type === 'IEND') { ended = size === 0; break; }
  }
  if (!hasHeader || !hasImage || !ended || offset !== png.length) throw new Error('PNG 数据不完整');
  return concat(parts);
}

export async function posterPng(canvas: HTMLCanvasElement): Promise<Blob> {
  const png = await canvasBlob(canvas, 'image/png');
  return new Blob([pngWithDpi(new Uint8Array(await png.arrayBuffer()))], { type: 'image/png' });
}

/** PDF 只封装一张 RGB JPEG；偏移按实际字节累计，不能用含二进制的字符串长度代替。 */
export function jpegToA4Pdf(jpeg: Uint8Array, width: number, height: number): Uint8Array<ArrayBuffer> {
  if (jpeg[0] !== 0xff || jpeg[1] !== 0xd8 || jpeg[jpeg.length - 2] !== 0xff || jpeg[jpeg.length - 1] !== 0xd9) throw new Error('海报不是有效的 JPEG');
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width < 1 || height < 1) throw new RangeError('海报尺寸无效');
  const pageWidth = '595.275591', pageHeight = '841.889764';
  const stream = encoder.encode(`q\n${pageWidth} 0 0 ${pageHeight} 0 0 cm\n/Im0 Do\nQ\n`);
  const parts: Uint8Array[] = [encoder.encode('%PDF-1.4\n%\u00e2\u00e3\u00cf\u00d3\n')];
  const offsets = [0];
  let length = parts[0]!.length;
  const append = (bytes: Uint8Array): void => { parts.push(bytes); length += bytes.length; };
  const object = (id: number, body: readonly Uint8Array[]): void => {
    offsets[id] = length;
    append(encoder.encode(`${id} 0 obj\n`));
    for (const bytes of body) append(bytes);
    append(encoder.encode('\nendobj\n'));
  };
  object(1, [encoder.encode('<< /Type /Catalog /Pages 2 0 R >>')]);
  object(2, [encoder.encode('<< /Type /Pages /Kids [3 0 R] /Count 1 >>')]);
  object(3, [encoder.encode(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`)]);
  object(4, [encoder.encode(`<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`), jpeg, encoder.encode('\nendstream')]);
  object(5, [encoder.encode(`<< /Length ${stream.length} >>\nstream\n`), stream, encoder.encode('endstream')]);
  const xref = length;
  append(encoder.encode(`xref\n0 6\n0000000000 65535 f \n${offsets.slice(1).map(offset => `${String(offset).padStart(10, '0')} 00000 n \n`).join('')}trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`));
  return concat(parts);
}

export async function posterPdf(canvas: HTMLCanvasElement): Promise<Blob> {
  const jpeg = await canvasBlob(canvas, 'image/jpeg', 0.96);
  return new Blob([jpegToA4Pdf(new Uint8Array(await jpeg.arrayBuffer()), canvas.width, canvas.height)], { type: 'application/pdf' });
}
